/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { StreamProgressData } from '../../../../services/file-transport/interfaces';
import type { TransportMiddlewareContext, TransportMiddlewareRequest, TransportMiddlewareResponse, NextFunction } from '../../../../core';

export const buildRequestInitForStreamUploadMiddleware = <T, R, E>(
    next: NextFunction<TransportMiddlewareResponse<R, E> | Error>,
    ctx: TransportMiddlewareContext,
    req: TransportMiddlewareRequest<T>,
    res: TransportMiddlewareResponse<R, E>
) => {
    const { commands } = ctx;
    const { streamData, options } = req;
    const { uploadFilesData, progress, fileNumber, chunkProgress, uploadedFilesSize, fullSize, totalFiles } = streamData as StreamProgressData;
    const fileData = uploadFilesData[fileNumber - 1];

    commands.setLastActiveTime(); // Should be on run step (because of sending multiple files or chunks)

    if (Array.isArray(fileData)) {
        // chunks way
        const { fileId } = fileData[0];

        if (!chunkProgress[fileId]) {
            chunkProgress[fileId] = {
                chunkNumber: 1,
                totalChunks: fileData.length,
            };
        }

        const { chunkNumber, totalChunks } = chunkProgress[fileId];
        const chunkData = fileData[chunkNumber - 1];

        req.requestInit = {
            ...options.requestInit,
            headers: {
                ...options.requestInit.headers,
                ...chunkData.headers,
            },
            body: chunkData.chunk,
            progress: (event: ProgressEvent<EventTarget>): void => {
                const currentProgress = event.loaded;

                if (typeof progress === 'function') {
                    const uploadedSize = uploadedFilesSize + currentProgress;

                    progress({
                        done: Math.floor((uploadedSize / fullSize) * 100), // in percents
                        doneForFile: Math.floor((currentProgress / chunkData.chunkSize) * 100), // in percents
                        total: 100,
                        chunkNumber,
                        totalChunks,
                        fileNumber: fileNumber,
                        totalFiles: totalFiles,
                    });
                }
            },
        };

    } else {
        // full file way
        req.requestInit = {
            ...options.requestInit,
            headers: {
                ...options.requestInit.headers,
                ...fileData.headers,
            },
            body: fileData.file,
            progress: (event: ProgressEvent<EventTarget>): void => {
                const currentProgress = event.loaded;

                if (typeof progress === 'function') {
                    const uploadedSize = uploadedFilesSize + currentProgress;

                    progress({
                        done: Math.floor((uploadedSize / fullSize) * 100), // in percents
                        doneForFile: Math.floor((currentProgress / fileData.fileSize) * 100), // in percents
                        total: 100,
                        chunkNumber: 1,
                        totalChunks: 1,
                        fileNumber: fileNumber,
                        totalFiles: totalFiles,
                    });
                }
            },
        };
    }

    next();
};
